import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  AlertTriangle,
  AlertOctagon,
  AlertCircle,
  Package,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ critical: 0, high: 0, medium: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/alerts/low-stock`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch alerts");
      const data = await res.json();
      setAlerts(data.alerts || []);
      setSummary(data.summary || { critical: 0, high: 0, medium: 0, total: 0 });
    } catch (e) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); }, []);

  const severityConfig = {
    critical: { icon: AlertOctagon, color: "text-red-500", bg: "bg-red-500/10", badge: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400", label: "Out of Stock" },
    high: { icon: AlertTriangle, color: "text-orange-500", bg: "bg-orange-500/10", badge: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400", label: "Critical Low" },
    medium: { icon: AlertCircle, color: "text-yellow-500", bg: "bg-yellow-500/10", badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400", label: "Low Stock" },
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="alerts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Low Stock Alerts</h1>
            <p className="text-muted-foreground">Monitor inventory levels and take action</p>
          </div>
          <Button onClick={fetchAlerts} variant="outline" className="rounded-sm" data-testid="refresh-alerts-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { key: "total", label: "Total Alerts", color: "text-foreground", bg: "bg-muted" },
            { key: "critical", label: "Out of Stock", color: "text-red-500", bg: "bg-red-500/10" },
            { key: "high", label: "Critical Low", color: "text-orange-500", bg: "bg-orange-500/10" },
            { key: "medium", label: "Low Stock", color: "text-yellow-500", bg: "bg-yellow-500/10" },
          ].map((item) => (
            <Card key={item.key} className="rounded-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-md ${item.bg}`}>
                  <AlertTriangle className={`h-5 w-5 ${item.color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{item.label}</p>
                  <p className="text-2xl font-semibold font-data">{summary[item.key] || 0}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Alerts Table */}
        <Card className="rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Inventory Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
                <p className="text-lg font-medium text-emerald-600">All Good!</p>
                <p className="text-muted-foreground">No low stock alerts. Inventory levels are healthy.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right font-data">Current Qty</TableHead>
                    <TableHead className="text-right font-data">Threshold</TableHead>
                    <TableHead className="text-right font-data">Shortage</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert, i) => {
                    const config = severityConfig[alert.severity] || severityConfig.medium;
                    return (
                      <TableRow key={i} data-testid={`alert-row-${i}`}>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${config.badge}`}>
                            <config.icon className="h-3 w-3" />
                            {config.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{alert.product_name}</p>
                            <p className="text-xs text-muted-foreground font-data">{alert.product_sku}</p>
                          </div>
                        </TableCell>
                        <TableCell>{alert.location_name}</TableCell>
                        <TableCell className="text-right font-data font-semibold">
                          <span className={alert.quantity === 0 ? "text-red-500" : "text-orange-500"}>
                            {alert.quantity}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-data">{alert.threshold}</TableCell>
                        <TableCell className="text-right font-data text-red-500 font-semibold">
                          -{alert.shortage}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/inventory`)}
                            data-testid={`alert-action-${i}`}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
